import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Star, Scale, Target, Trophy, GraduationCap, Newspaper, Flame, FileText } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { api } from "../lib/api";
import { VPNS } from "../data/vpns";
import { COMPARISONS } from "../data/comparisons";
import { BEST_FOR } from "../data/bestFor";
import { RANKINGS } from "../data/rankings";
import { LEARN } from "../data/learn";

const vpnName = (slug) => VPNS.find((v) => v.slug === slug)?.name || slug;

const STATIC_INDEX = [
  { group: "Pages", icon: Flame, items: [
    { title: "VPN Deals — Verified Monthly", to: "/deals", keywords: "discount coupon offer sale cheap" },
    { title: "Best VPNs 2026 — Top 5 Ranked", to: "/best-vpns", keywords: "top ranked overall winner" },
  ]},
  { group: "Reviews", icon: Star, items: VPNS.map((v) => ({
    title: `${v.name} Review 2026`, to: `/reviews/${v.slug}`, keywords: `${v.tagline || ""} review test`,
  }))},
  { group: "Comparisons", icon: Scale, items: COMPARISONS.map((c) => ({
    title: `${vpnName(c.a)} vs ${vpnName(c.b)}`, to: `/compare/${c.slug}`, keywords: "compare versus head to head",
  }))},
  { group: "Best VPN For", icon: Target, items: BEST_FOR.map((b) => ({
    title: b.title, to: `/best-vpn-for/${b.slug}`, keywords: b.label,
  }))},
  { group: "Rankings", icon: Trophy, items: RANKINGS.map((r) => ({
    title: r.title, to: `/rankings/${r.slug}`, keywords: `${r.label} ranking`,
  }))},
  { group: "Learn", icon: GraduationCap, items: LEARN.map((l) => ({
    title: l.title, to: `/learn/${l.slug}`, keywords: `${l.label} guide explained`,
  }))},
];

let postsCache = null;

export const SiteSearch = () => {
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState(postsCache || []);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (!open || postsCache) return;
    api
      .get("/posts")
      .then(({ data }) => {
        postsCache = data;
        setPosts(data);
      })
      .catch(() => {});
  }, [open]);

  const groups = useMemo(() => {
    const g = [...STATIC_INDEX];
    if (posts.length) {
      g.push({
        group: "Blog",
        icon: Newspaper,
        items: posts.map((p) => ({
          title: p.title,
          to: `/blog/${p.slug}`,
          keywords: `${p.category || ""} ${p.excerpt || ""}`.slice(0, 120),
        })),
      });
    }
    return g;
  }, [posts]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="site-search-button"
        aria-label="Search the site"
        className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.06] px-3 py-2 text-[13px] font-semibold text-slate-300 transition-colors duration-150 hover:border-white/30 hover:text-white"
      >
        <Search size={14} />
        <span className="hidden lg:inline">Search</span>
        <kbd className="hidden rounded border border-white/15 px-1.5 py-0.5 text-[10px] font-bold text-slate-400 lg:inline">
          ⌘K
        </kbd>
      </button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search reviews, comparisons, guides…" data-testid="site-search-input" />
        <CommandList className="max-h-[420px]" data-testid="site-search-results">
          <CommandEmpty>No results found.</CommandEmpty>
          {groups.map(({ group, icon: Icon, items }) => (
            <CommandGroup key={group} heading={group}>
              {items.map((item) => (
                <CommandItem
                  key={item.to}
                  value={`${item.title} ${item.keywords}`}
                  onSelect={() => go(item.to)}
                  data-testid={`search-result-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className="cursor-pointer"
                >
                  <Icon className="mr-2 shrink-0 text-[#4687FF]" />
                  <span className="truncate">{item.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {!posts.length && (
            <CommandGroup heading="Blog">
              <CommandItem value="blog loading" disabled>
                <FileText className="mr-2 shrink-0 text-slate-400" />
                Loading blog posts…
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
